import React, { useEffect, useState } from "react"
import {
  Modal,
  Typography,
  Form,
  Select,
  Radio,
  Input,
  InputNumber,
  Collapse,
  Alert,
  Checkbox,
} from "antd"
import { useNavigate } from "react-router-dom"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"
import IntegrationReportView from "./IntegrationReport.component"

import {
  useIntegrateDataMutation,
  IntegrationFailedError,
} from "features/DataIntegration/api/useDataIntegration"
import { useProjectsQuery } from "features/Projects/api/useProjectsQuery"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import { datafileGet } from "api/Repos/data-import"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"

import { PROJECT_FILTER } from "types/project.types"
import { DATAFILE_TYPE } from "types/dataImport.types"
import {
  ADD_KEEP_OPTION,
  CONNECT_JOIN_OPTION,
  INTEGRATION_OPERATION,
  IntegrateDataParams,
  IntegrationFailure,
  IntegrationOnLoad,
  IntegrationReport,
} from "types/dataIntegration.types"

import { notify } from "shared/ui/Notifications/notify"
import { ROUTES } from "constants/routes"

interface IntegrateFormValues {
  baseId: string
  addId: string
  operation: INTEGRATION_OPERATION
  keep?: ADD_KEEP_OPTION
  join?: CONNECT_JOIN_OPTION
  idPath?: string
  name: string
  deriveQuery?: string
  firstWave?: number
  applyOnLoad: boolean
}

const keepOptions = [
  { label: "All variables", value: ADD_KEEP_OPTION.ALL },
  { label: "Original only", value: ADD_KEEP_OPTION.ORIGINAL },
  { label: "Added only", value: ADD_KEEP_OPTION.ADD },
  { label: "In both", value: ADD_KEEP_OPTION.BOTH },
]

const joinOptions = [
  { label: "All respondents", value: CONNECT_JOIN_OPTION.ALL },
  { label: "In both", value: CONNECT_JOIN_OPTION.BOTH },
  { label: "Original only", value: CONNECT_JOIN_OPTION.ORIGINAL },
]

/**
 * Probes whether a project has a combined Data(Q) data file (.dqd) so the
 * modal can warn before a merge that is guaranteed to fail. Mirrors the
 * size-probe used by the Data Import flow. Returns null while unknown.
 *
 * @param id - The project ID (undefined until a project is picked).
 * @param owner - The project owner.
 * @returns true/false once probed, null while unknown or on error.
 */
const useDqdPresence = (
  id: string | undefined,
  owner: string | undefined
): boolean | null => {
  const getAxios = useAuthedAxios()
  const [present, setPresent] = useState<boolean | null>(null)

  useEffect(() => {
    if (!id || !owner) {
      setPresent(null)
      return
    }
    let cancelled = false
    const probe = async (): Promise<void> => {
      try {
        const res = await datafileGet(
          await getAxios(),
          id,
          owner,
          DATAFILE_TYPE.DQD,
          "size"
        )
        if (!cancelled) setPresent(typeof res.data === "number")
      } catch {
        if (!cancelled) setPresent(null)
      }
    }
    probe()
    return (): void => {
      cancelled = true
    }
  }, [id, owner, getAxios])

  return present
}

interface IntegrateDataModalProps {
  open: boolean
  onClose: () => void
}

/**
 * IntegrateDataModal component - A modal for merging two projects'
 * Data(Q) data files into a NEW data-only project.
 *
 * The user picks a base project and a second project, chooses whether to
 * append cases (add) or join by an ID path (connect) with the relevant
 * options, and names the new project. On success the modal swaps to the
 * integration report with a link to the new project. The project list
 * refreshes via the mutation's cache invalidation. The caller opens it (the
 * Projects header's New menu) and is told when it closes.
 *
 * @component
 * @returns  The IntegrateDataModal component.
 */
const IntegrateDataModal: React.FC<IntegrateDataModalProps> = ({
  open,
  onClose,
}) => {
  const { Title } = Typography
  const [form] = Form.useForm<IntegrateFormValues>()
  const [report, setReport] = useState<IntegrationReport | null>(null)
  const [newProjectId, setNewProjectId] = useState<string | null>(null)
  const [failure, setFailure] = useState<IntegrationFailure | null>(null)
  const [notRecorded, setNotRecorded] = useState<boolean>(false)
  // Echoed back by the server so the report can name the buckets by wave.
  const [derivedWaves, setDerivedWaves] = useState<{
    base?: number
    add?: number
  } | null>(null)
  const [onLoad, setOnLoad] = useState<IntegrationOnLoad | null>(null)
  const [queries, setQueries] = useState<string[]>([])

  const navigate = useNavigate()
  const integrateMutation = useIntegrateDataMutation()
  const { getProjectQueries } = useSavedQueries()
  const { data: projects = [], isLoading: isLoadingProjects } = useProjectsQuery(
    PROJECT_FILTER.BOTH,
    open
  )

  const operation = Form.useWatch("operation", form)
  const baseId = Form.useWatch("baseId", form)
  const addId = Form.useWatch("addId", form)
  const firstWave = Form.useWatch("firstWave", form)
  const isConnect = operation === INTEGRATION_OPERATION.CONNECT

  const ownerById = (id: string | undefined): string =>
    projects.find((project) => project.id === id)?.owner ?? ""

  const baseHasDqd = useDqdPresence(baseId, ownerById(baseId))
  const addHasDqd = useDqdPresence(addId, ownerById(addId))

  const projectOptions = projects.map((project) => ({
    label: `${project.name} (${project.owner})`,
    value: project.id,
  }))

  // The base project's saved queries — one of them is run against BOTH sides, so the
  // two waves end up with the same definitions. Same source the Provenance ▸ Lineage
  // picker reads (newline-separated names).
  const baseOwner = ownerById(baseId)
  useEffect(() => {
    if (!baseId) {
      setQueries([])
      return
    }
    let cancelled = false
    getProjectQueries(baseId, baseOwner)
      .then((res) => {
        if (cancelled) return
        setQueries(
          (res.data || "")
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
        )
      })
      .catch(() => {
        if (!cancelled) setQueries([])
      })
    return (): void => {
      cancelled = true
    }
    // getProjectQueries identity is stable enough; keyed on the base project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseId, baseOwner])

  // Wave stamping only means something when the two files hold DIFFERENT respondents.
  // Switching to connect clears it so a stale value cannot be submitted (the server
  // rejects the combination outright).
  useEffect(() => {
    if (isConnect) form.setFieldValue("firstWave", undefined)
  }, [isConnect, form])

  /**
   * Resets the form and report state and hides the modal.
   */
  const resetAndClose = (): void => {
    form.resetFields()
    setReport(null)
    setNewProjectId(null)
    setFailure(null)
    setNotRecorded(false)
    setDerivedWaves(null)
    setOnLoad(null)
    onClose()
  }

  /**
   * Navigates to the newly created project, then closes the modal.
   */
  const openProject = (): void => {
    if (newProjectId) {
      navigate(ROUTES.PROJECT(newProjectId))
    }
    resetAndClose()
  }

  /**
   * Submits the integration form: runs the merge, then swaps to the report.
   * Errors are already surfaced to the user by the mutation (handleSequenceError
   * / onError), so this only logs for debugging.
   *
   * @async
   * @function
   * @param {IntegrateFormValues} values - The form values.
   * @returns {Promise<void>}
   */
  const submitIntegrateForm = async (
    values: IntegrateFormValues
  ): Promise<void> => {
    const params: IntegrateDataParams = {
      baseId: values.baseId,
      baseOwner: ownerById(values.baseId),
      addId: values.addId,
      addOwner: ownerById(values.addId),
      operation: values.operation,
      name: values.name,
      ...(values.operation === INTEGRATION_OPERATION.ADD
        ? { keep: values.keep }
        : { join: values.join, idPath: values.idPath }),
      ...(values.deriveQuery
        ? {
            deriveQuery: values.deriveQuery,
            querySourceId: values.baseId,
            querySourceOwner: ownerById(values.baseId),
          }
        : {}),
      ...(values.operation === INTEGRATION_OPERATION.ADD &&
      values.firstWave !== undefined &&
      values.firstWave !== null
        ? { firstWave: values.firstWave }
        : {}),
      applyOnLoad: values.applyOnLoad,
    }

    setFailure(null)
    try {
      const response = await integrateMutation.mutateAsync(params)
      notify.toast.success("Data integrated successfully!")
      setReport(response.report ?? null)
      setNewProjectId(response.project_id)
      setNotRecorded(response.recorded === false)
      setDerivedWaves(
        response.derive
          ? { base: response.derive.base_wave, add: response.derive.add_wave }
          : null
      )
      setOnLoad(response.onload ?? null)
    } catch (error) {
      // A derive script that stopped part-way comes back with the engine's log. The
      // merge was cancelled rather than saved half-derived, so keep the form filled in
      // and show which line failed.
      if (error instanceof IntegrationFailedError && error.failure) {
        setFailure(error.failure)
      }
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      console.error("Error integrating data:", errorMessage)
    }
  }

  return (
    <>
      <ProgressModal
        title="Integrating Data"
        isLoading={integrateMutation.isPending}
        duration={3200}
        indeterminate
        subtitle="Preparing each project, merging, then saving the result. Large waves can take a few minutes."
      />

      <Modal
        open={open}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
      >
        {newProjectId ? (
          <>
            <Title style={{ marginTop: "0.5em" }} level={4}>
              Integration Report
            </Title>
            {report ? (
              <IntegrationReportView
                report={report}
                baseWave={derivedWaves?.base}
                addWave={derivedWaves?.add}
              />
            ) : (
              <p className="text-sm text-gray-600">
                The data was integrated successfully.
              </p>
            )}
            {onLoad ? (
              <Alert
                className="mt-3"
                type={onLoad.warning ? "warning" : "info"}
                showIcon
                message="Run on load scripts applied before the merge"
                description={
                  <>
                    <p className="m-0 text-sm">
                      Base project:{" "}
                      {onLoad.base.length ? onLoad.base.join(", ") : "none"}
                    </p>
                    <p className="m-0 text-sm">
                      Second project:{" "}
                      {onLoad.add.length ? onLoad.add.join(", ") : "none"}
                    </p>
                    <p className="mb-0 mt-1 text-xs text-gray-500">
                      Their variables are part of the new project's data.
                    </p>
                    {onLoad.warning ? (
                      <p className="mb-0 mt-1 text-sm">{onLoad.warning}</p>
                    ) : null}
                  </>
                }
              />
            ) : null}
            {notRecorded ? (
              <Alert
                className="mt-3"
                type="warning"
                showIcon
                message="Provenance was not recorded"
                description="The merge succeeded, but the record naming the two source projects could not be written. The new project's Provenance tab will not show where it came from."
              />
            ) : null}
            <div className="flex flex-row mt-5">
              <div className="ml-auto mr-2 space-x-2">
                <DataQButton type="primary" onClick={openProject}>
                  Open Project
                </DataQButton>
                <DataQButton type="secondary" onClick={resetAndClose}>
                  Close
                </DataQButton>
              </div>
            </div>
          </>
        ) : (
          <>
            <Title style={{ marginTop: "0.5em" }} level={4}>
              Integrate Data
            </Title>
            {failure ? (
              <Alert
                className="mb-4"
                type="error"
                showIcon
                message="The merge was cancelled — nothing was saved"
                description={
                  <>
                    <p className="mb-2">
                      {failure.stage.startsWith("wave-")
                        ? "The WAVE variable could not be added to the "
                        : failure.stage.startsWith("onload-")
                          ? failure.query
                            ? `The Run on load script "${failure.query}" stopped on the `
                            : "Run on load could not run on the "
                          : `The script "${failure.query ?? ""}" stopped part-way through the `}
                      {failure.stage.endsWith("-base")
                        ? "base project"
                        : "second project"}
                      . The last line below is the failure.
                    </p>
                    <pre className="max-h-40 overflow-auto text-xs bg-gray-50 p-2 rounded m-0">
                      {failure.log.map((line, i) => (
                        <div
                          key={i}
                          className={
                            i === failure.log.length - 1
                              ? "text-red-600 font-semibold"
                              : undefined
                          }
                        >
                          {line}
                        </div>
                      ))}
                    </pre>
                  </>
                }
              />
            ) : null}
            <Form<IntegrateFormValues>
              form={form}
              name="integrate-data-form"
              onFinish={submitIntegrateForm}
              style={{ textAlign: "left" }}
              layout="vertical"
              initialValues={{
                operation: INTEGRATION_OPERATION.ADD,
                keep: ADD_KEEP_OPTION.ALL,
                join: CONNECT_JOIN_OPTION.ALL,
                applyOnLoad: true,
              }}
            >
              <Form.Item
                label="Base project"
                name="baseId"
                validateTrigger="onSubmit"
                rules={[
                  { required: true, message: "Please select the base project" },
                ]}
                extra={
                  baseHasDqd === false ? (
                    <span className="text-amber-600 text-xs">
                      This project has no combined Data(Q) file (.dqd) yet.
                    </span>
                  ) : undefined
                }
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select the base project"
                  loading={isLoadingProjects}
                  options={projectOptions}
                />
              </Form.Item>

              <Form.Item
                label="Second project"
                name="addId"
                dependencies={["baseId"]}
                validateTrigger="onSubmit"
                rules={[
                  {
                    required: true,
                    message: "Please select the second project",
                  },
                  {
                    validator: (_rule, value) =>
                      value && value === form.getFieldValue("baseId")
                        ? Promise.reject(
                            new Error("The two projects must be different")
                          )
                        : Promise.resolve(),
                  },
                ]}
                extra={
                  addHasDqd === false ? (
                    <span className="text-amber-600 text-xs">
                      This project has no combined Data(Q) file (.dqd) yet.
                    </span>
                  ) : undefined
                }
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select the second project"
                  loading={isLoadingProjects}
                  options={projectOptions}
                />
              </Form.Item>

              <Form.Item label="Operation" name="operation">
                <Radio.Group>
                  <Radio value={INTEGRATION_OPERATION.ADD}>
                    Append cases (add)
                  </Radio>
                  <Radio value={INTEGRATION_OPERATION.CONNECT}>
                    Join by ID (connect)
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {isConnect ? (
                <>
                  <Form.Item label="Keep respondents" name="join">
                    <Select options={joinOptions} />
                  </Form.Item>
                  <Form.Item
                    label="ID variable path"
                    name="idPath"
                    validateTrigger="onSubmit"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the ID variable path",
                      },
                    ]}
                  >
                    <Input placeholder="Section//Respondent ID" />
                  </Form.Item>
                </>
              ) : (
                <Form.Item label="Keep variables" name="keep">
                  <Select options={keepOptions} />
                </Form.Item>
              )}

              <Form.Item
                label="New project name"
                name="name"
                validateTrigger="onSubmit"
                rules={[
                  {
                    required: true,
                    message: "Please enter a name for the new project",
                  },
                ]}
              >
                <Input placeholder="New Project Name" />
              </Form.Item>

              <Form.Item
                name="applyOnLoad"
                valuePropName="checked"
                extra="Each project runs its own Run on load scripts first, so their variables become part of the new project's data. Untick to merge the data as imported."
              >
                <Checkbox>Apply Run on load scripts</Checkbox>
              </Form.Item>

              <Collapse
                ghost
                size="small"
                className="mb-2"
                items={[
                  {
                    key: "derive",
                    label: (
                      <span className="text-sm">Advanced — derive & wave</span>
                    ),
                    children: (
                      <>
                        <Form.Item
                          label="Derive script"
                          name="deriveQuery"
                          extra="Runs against each project before the merge, so both waves are built from the same definitions. Deriving after the merge instead leaves the earlier wave carrying blanks."
                        >
                          <Select
                            allowClear
                            placeholder={
                              queries.length
                                ? "None — merge the files as they are"
                                : "No saved queries in the base project"
                            }
                            disabled={!queries.length}
                            options={queries.map((q) => ({
                              label: q,
                              value: q,
                            }))}
                          />
                        </Form.Item>

                        {isConnect ? (
                          <p className="text-xs text-gray-500">
                            Wave stamping applies to append (add) only — connect
                            joins the same respondents.
                          </p>
                        ) : (
                          <Form.Item
                            label="First wave number"
                            name="firstWave"
                            extra={
                              firstWave === undefined || firstWave === null
                                ? "Adds an INT variable WAVE to each file so the merged data can be split by wave."
                                : `Base = WAVE ${firstWave}, second project = WAVE ${firstWave + 1}`
                            }
                          >
                            <InputNumber
                              min={0}
                              style={{ width: "100%" }}
                              placeholder="e.g. 5"
                            />
                          </Form.Item>
                        )}
                      </>
                    ),
                  },
                ]}
              />

              <div className="flex flex-row mt-5">
                <div className="ml-auto mr-2 space-x-2">
                  <DataQButton
                    type="primary"
                    htmlType="submit"
                    isLoading={integrateMutation.isPending}
                  >
                    Integrate
                  </DataQButton>
                  <DataQButton onClick={resetAndClose} type="secondary">
                    Cancel
                  </DataQButton>
                </div>
              </div>
            </Form>
          </>
        )}
      </Modal>
    </>
  )
}

export default IntegrateDataModal
